import { useCallback, useContext, useEffect, useState } from "react";
import { useId } from "react-aria";

import { BTPopover } from "@btnet/core-components/BTPopover";
import { cn } from "@btnet/utilities-core";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { showAPIErrorMessage } from "utilities/apiHandler";
import { isInPortal } from "utilities/portal/portal";

import { BTAvatarDeprecated } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated";
import { type BTAvatarDeprecatedSize } from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated.types";
import {
    getAvatarColor,
    getAvatarColorDark,
    isValidUserId,
    sizeNameToPixels,
} from "commonComponents/btWrappers/BTAvatarDeprecated/BTAvatarDeprecated.utils";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTUserInfoDisplay } from "commonComponents/btWrappers/BTUser/BTUserInfoDisplay";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import {
    type IUserActivationPageHandler,
    UserActivationPageHandler,
} from "entity/LinkAccountActivation/UserActivationPage.api.handler";
import { type UserProfileResponse } from "entity/login/Login/Login.api.types";

import "./BTUser.less";

interface IBTUserProps {
    /**
     * @default middle
     */
    size?: BTAvatarDeprecatedSize;

    displayName: string;
    email?: string;
    subtitle?: string;
    phoneCell?: string;

    globalUserId: number;

    /**
     * Show avatar only or avatar AND name (full)
     * @default avatar
     */
    type?: "mini" | "full";

    /**
     * Show the avatars initials
     * @default true
     */
    showInitials?: boolean;

    /**
     * When to show the contact card
     * @default hover
     */
    contactCard?: "none" | "hover" | "click";

    style?: React.CSSProperties;
    className?: string;

    handler?: IUserActivationPageHandler;
    onFullNameClick?: (globalUserId: number) => void;
    boldCurrentUser?: boolean;
    /**
     * Ids of elements that describe the user, such as an approval status. Screen readers read them
     * after the name.
     */
    "aria-describedby"?: string;
}

const defaultUserHandler = new UserActivationPageHandler();

export const BTUser: React.FunctionComponent<IBTUserProps & ITrackingProp> = track((props) => ({
    element: "User",
    uniqueId: props.globalUserId,
}))(({
    size = "middle",
    displayName,
    email,
    subtitle,
    phoneCell,
    globalUserId,
    type = "mini",
    showInitials = true,
    contactCard = "hover",
    style,
    className,
    handler = defaultUserHandler,
    tracking,
    onFullNameClick,
    boldCurrentUser = true,
    "aria-describedby": ariaDescribedBy,
}) => {
    const subtitleId = useId();
    const [userProfile, setUserProfile] = useState<UserProfileResponse | undefined>(undefined);

    useEffect(() => {
        setUserProfile(undefined);
    }, [globalUserId]);

    // We use global user id = 3 for some reason on Bills/POs when it could be a real user
    const isMiscBillPOUser = globalUserId === 3 && displayName === "Misc";
    const isRealUser = isValidUserId(globalUserId) && !isMiscBillPOUser;

    const handleLoadUserProfile = useCallback(async () => {
        try {
            tracking?.trackEvent({ event: "PopoverOpen" });

            let userProfile: UserProfileResponse | undefined = undefined;
            if (!isRealUser) {
                userProfile = {
                    name: displayName,
                    userType: BTLoginTypes.ALL,
                    phone: "",
                    email: "",
                    title: "",
                    cellPhone: "",
                    primaryContact: "",
                    canStartChatConversation: false,
                };
            } else {
                userProfile = await handler.profile(globalUserId);
            }
            setUserProfile(userProfile);
        } catch (e) {
            showAPIErrorMessage(e);
        }
    }, [tracking, isRealUser, displayName, handler, globalUserId]);

    const handleVisibleChange = useCallback(
        (visible: boolean) => {
            if (visible && userProfile === undefined) {
                void handleLoadUserProfile();
            }
        },
        [handleLoadUserProfile, userProfile],
    );

    // todo <ContactInfoDisplay> instead?
    const handleGetUserContent = useCallback(() => {
        return (
            <BTUserInfoDisplay
                showManageUser
                hideAllControls={!isRealUser}
                {...{ globalUserId, displayName, userProfile, isMiscBillPOUser }}
            />
        );
    }, [displayName, globalUserId, isMiscBillPOUser, isRealUser, userProfile]);

    const userInfo = useContext(UserInfoContext);
    const currentUserId = userInfo?.globalUserId;

    const isCurrentUser = currentUserId === globalUserId;
    const sizeInPixels = sizeNameToPixels(size);
    const fontSizeInPixels = Math.max(sizeInPixels / 2 - 2, 14);

    const isBuilder = isInPortal({ builder: true });
    const isDisabled = !isBuilder || !displayName || contactCard === "none";
    const [isContactCardOpen, setIsContactCardOpen] = useState(false);
    const handleContactCardVisibleChange = (visible: boolean) => {
        setIsContactCardOpen(visible);
        handleVisibleChange(visible);
    };
    // The card opens on hover and focus. Enter and Space open it too, since the chip says it is a
    // button, and Escape closes it.
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleContactCardVisibleChange(true);
        } else if (e.key === "Escape" && isContactCardOpen) {
            // Closing the card uses up this Escape, so a modal around the chip stays open.
            e.stopPropagation();
            handleContactCardVisibleChange(false);
        }
    };
    // A plain join: cn's tailwind-merge would drop an id that looks like a utility class.
    const describedBy =
        [type === "full" && subtitle && subtitleId, ariaDescribedBy].filter(Boolean).join(" ") ||
        undefined;

    return (
        <BTPopover
            trigger={contactCard === "click" ? ["click", "focus"] : ["hover", "focus"]}
            widthType="unrestricted"
            visible={isContactCardOpen}
            onVisibleChange={handleContactCardVisibleChange}
            content={handleGetUserContent}
            disabled={isDisabled}
            mouseEnterDelay={0.5}
            mouseLeaveDelay={0.2}
        >
            <BTRow
                gutter={8}
                className={cn("BTUser", className)}
                role={isDisabled ? undefined : "button"}
                tabIndex={isDisabled ? undefined : 0}
                // Named by the person alone: the avatar's initials would repeat it.
                aria-label={isDisabled ? undefined : displayName}
                aria-expanded={isDisabled ? undefined : isContactCardOpen}
                aria-describedby={describedBy}
                onKeyDown={isDisabled ? undefined : handleKeyDown}
                data-testid={`BTUser-${globalUserId}`}
            >
                {/* Hidden only when the chip's own label names the person; otherwise the
                    avatar's initials are all a screen reader has. */}
                <BTCol aria-hidden={isDisabled ? undefined : true}>
                    <BTAvatarDeprecated
                        className="BTUserAvatarDisplay"
                        size={size}
                        style={{
                            backgroundColor: getAvatarColor(globalUserId),
                            color: getAvatarColorDark(globalUserId),
                            ...style,
                        }}
                        displayName={showInitials ? displayName : ""}
                    />
                </BTCol>
                {type === "full" && (
                    <BTCol className="BTUser-Name-Display-details">
                        <div
                            className={cn({
                                "BTUser isCurrentUser":
                                    (isCurrentUser || subtitle || email || phoneCell) &&
                                    boldCurrentUser,
                            })}
                            style={{ fontSize: fontSizeInPixels }}
                            onClick={() =>
                                onFullNameClick ? onFullNameClick(globalUserId) : undefined
                            }
                        >
                            <TextTruncate>{displayName}</TextTruncate>
                        </div>
                        {subtitle && (
                            <div id={subtitleId} className="BTUser details">
                                {subtitle}
                            </div>
                        )}
                        {email && <div className="BTUser details">{email}</div>}
                        {phoneCell && <div className="BTUser details">{phoneCell}</div>}
                    </BTCol>
                )}
            </BTRow>
        </BTPopover>
    );
});
